import React, { createContext, useCallback, useContext, useState } from 'react';
import { Alert, AlertTitle, CircularProgress, Snackbar } from '@mui/material';
import { normalizeStatusPayload } from '../utils/statusMessage';

const NotificationContext = createContext();
const DEFAULT_DURATION = 5000;

const TOAST_SEVERITY_TO_SX = {
  success: {
    background: 'linear-gradient(135deg, #e8fff3 0%, #d2f8e5 100%)',
    border: '1px solid rgba(22, 163, 74, 0.28)',
    color: '#166534',
  },
  error: {
    background: 'linear-gradient(135deg, #fff1f1 0%, #ffe0e0 100%)',
    border: '1px solid rgba(220, 38, 38, 0.28)',
    color: '#991b1b',
  },
  warning: {
    background: 'linear-gradient(135deg, #fff8e7 0%, #ffefc7 100%)',
    border: '1px solid rgba(217, 119, 6, 0.28)',
    color: '#92400e',
  },
  info: {
    background: 'linear-gradient(135deg, #eef6ff 0%, #dfeeff 100%)',
    border: '1px solid rgba(37, 99, 235, 0.24)',
    color: '#1d4ed8',
  },
  loading: {
    background: 'linear-gradient(135deg, #f4f7fb 0%, #eaf0f8 100%)',
    border: '1px solid rgba(71, 85, 105, 0.22)',
    color: '#334155',
  },
};

export const NotificationProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showStatus = useCallback((type, message, options = {}) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const normalized = normalizeStatusPayload(type, message, options);

    setToasts((prev) => [
      ...prev,
      {
        id,
        duration: type === 'loading' ? null : (Number(options.duration) > 0 ? Number(options.duration) : DEFAULT_DURATION),
        ...normalized,
      },
    ]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ showStatus }}>
      {children}
      {toasts.map((toast, index) => {
        const severitySx = TOAST_SEVERITY_TO_SX[toast.type] || TOAST_SEVERITY_TO_SX.info;

        return (
          <Snackbar
            key={toast.id}
            open
            autoHideDuration={toast.duration}
            onClose={(_, reason) => {
              if (reason === 'clickaway') return;
              removeToast(toast.id);
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            sx={{
              bottom: `${24 + index * 96}px !important`,
              right: '16px !important',
              left: 'auto !important',
              width: { xs: 'calc(100% - 24px)', sm: '360px' },
              maxWidth: '360px',
            }}
          >
            <Alert
              severity={toast.type === 'loading' ? 'info' : toast.type}
              variant="filled"
              onClose={() => removeToast(toast.id)}
              sx={{
                width: '100%',
                alignItems: 'flex-start',
                borderRadius: '14px',
                boxShadow: '0 16px 40px rgba(15, 23, 42, 0.16)',
                backdropFilter: 'blur(14px)',
                '&.MuiAlert-filled': severitySx,
                '& .MuiAlert-icon': {
                  marginTop: '2px',
                },
                '& .MuiAlert-action': {
                  paddingTop: '2px',
                },
                '& .MuiAlert-message': {
                  width: '100%',
                  padding: 0,
                  whiteSpace: 'pre-line',
                },
              }}
              icon={toast.type === 'loading' ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              <AlertTitle sx={{ mb: 0.25, fontSize: '0.95rem', fontWeight: 700 }}>
                {toast.title}
              </AlertTitle>
              {toast.message}
            </Alert>
          </Snackbar>
        );
      })}
    </NotificationContext.Provider>
  );
};

export const useStatus = () => useContext(NotificationContext);
